import { Injectable } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import {
  stateDropdownCountries, zipCodeCountryMapping, stateDropdownRegions,
  regionDropDownCountries,
  countriesWithoutProvinceOrStateInput,
  countriesWithHiddenSTateOrProvinceField
} from 'core/app-info/country-specifics.enum';
import { KeyValuePair } from 'src/app/modules/shared/entities/key-value-pair.class';
import { CountryData } from 'src/app/modules/shared/entities/country-data.class';
import { Address } from 'src/app/modules/shared/entities/address.class';

@Injectable({
  providedIn: 'root'
})
export class AddressService {

  private urls = {
    stateList: '/api/contact/statevalues?all=true',
    countryList: '/api/contact/countryvalues',
    regionList: '/api/contact/regionvalues',
    countyList: '/api/contact/countyvalues'
  };

  private templateDataState: any;
  private templateCountryData: CountryData;
  private templateRegionData: any;
  private templateCountyData: any;

  constructor(
    private apiService: ApiService,
    private appInfoService: AppInfoService
  ) { }

  getTemplateValues(callback: (...args: any[]) => any, additionalUrls: string[] = []) {
    const observableList = [];

    if (this.templateCountryData && this.templateDataState && this.templateRegionData && this.templateCountyData) {
      observableList.push(...[
        of(this.templateCountryData),
        of(this.templateDataState), of(this.templateRegionData), of(this.templateCountyData)]);
    } else {
      observableList.push(...[
        this.apiService.get(this.urls.countryList),
        this.apiService.get(this.urls.stateList),
        this.apiService.get(this.urls.regionList),
        this.apiService.get(this.urls.countyList)
      ]);
    }

    additionalUrls.forEach(url => {
      observableList.push(this.apiService.get(url));
    });

    forkJoin(observableList).subscribe((results: any[]) => {
      this.templateCountryData = results[0];
      this.templateDataState = results[1];
      this.templateRegionData = results[2];
      this.templateCountyData = results[3];
      callback(results);
    });
  }

  /**
   * Returns if the country provided uses state dropdown
   */
  countryUsesStateDropdown(countryId: number) {
    return stateDropdownCountries.indexOf((this.templateCountryData.Countries).find(
      (country: KeyValuePair) => country.Key === countryId).Value) !== -1;
  }

  countryUsesRegionStateDropdown(countryId: number) {
    return regionDropDownCountries.indexOf((this.templateCountryData.Countries).find(
      (country: KeyValuePair) => country.Key === countryId).Value) !== -1;
  }

  countryUsesProvinceFreeInput(countryId) {
    const countryProvince: string = this.templateCountryData.Countries.find(country => country.Key === countryId).Value;
    return stateDropdownCountries.indexOf(countryProvince) === -1
      && regionDropDownCountries.indexOf(countryProvince) === -1
      && countriesWithoutProvinceOrStateInput.indexOf(countryProvince) === -1
      && countriesWithHiddenSTateOrProvinceField.indexOf(countryProvince) === -1;
  }

  processingFullContactName(fullContactName) {
    if (fullContactName) {
      const namesArray = fullContactName.split(' ');
      if (namesArray.length) {
        const firstName = namesArray[0];
        if (namesArray.length === 2) {
          const middleName = '';
          const lastName = namesArray[1];
          return ([firstName, middleName, lastName]);
        }
        if (namesArray.length > 2) {
          const middleName = namesArray[1];
          const lastName = namesArray.slice(2).join(' ');
          return ([firstName, middleName, lastName]);
        }

        return [firstName];
      }
    }
  }

  /**
   * Returns the country value by countryId
   */
  getCountryValueById(countryId) {
    return this.templateCountryData.Countries.find(country => country.Key === countryId)?.Value;
  }

  /**
   * Returns the countryId by value
   */
  getCountryIdByValue(countryVal) {
    return this.templateCountryData.Countries.find(country => country.Value === countryVal)?.Key;
  }

  /**
   * Normalizes Address values (states & zip code)
   */
  normalizeAddressStateAndZip(address: Address) {
    this.normalizeAddressState(address);
    this.normalizeAddressZip(address);
  }

  normalizeAddressState(address) {
    if (!this.hasValue(address.countryId) || this.countryUsesStateDropdown(address.countryId)) {
      address.province = null;
    } else {
      address.province.id = address.province.id || this.appInfoService.emptyGuid;
      address.province.name = address.province.name || '';
      address.stateId = null;
    }
  }

  private normalizeAddressZip(address) {
    if (address.zip) {
      address.zip = address.zip.toString();
    }
    const zipLengh = zipCodeCountryMapping[this.getCountryValueById(address.countryId)]?.length || zipCodeCountryMapping.Default.length;
    if (address.zip && address.zip.length && address.zip.length > zipLengh) {
      address.zip = address.zip.substring(0, zipLengh);
    }
  }

  /**
   * Rests fields when country has changed
   */
  countryChanged(newCountry, form) {
    if (!newCountry) {
      return;
    }

    // Update ZIP field and validators
    const zipController = (form.controls.address as FormGroup).controls.zip;
    const countryMapping = zipCodeCountryMapping[this.getCountryValueById(newCountry)] || zipCodeCountryMapping.Default;
    const zipCodeMaxLength = countryMapping.length;
    const zipCodeValidator: string = countryMapping.validator;

    const regExp = new RegExp(zipCodeValidator);
    if (!regExp.test(zipController.value as string)) {
      zipController.reset();
    }
    zipController.setValidators([Validators.maxLength(zipCodeMaxLength), Validators.pattern(zipCodeValidator)]);

    if (zipController.value && zipController.value.length > zipCodeMaxLength) {
      zipController.setValue(zipController.value.substring(0, zipCodeMaxLength));
    }
    zipController.updateValueAndValidity();
  }

  /**
   * Gets a mapped list of Country ID -> list of states
   */
  getStateListPerCountry(stateValues) {
    const stateList = [];
    stateDropdownCountries.forEach(countryVal => {
      const countryId = this.getCountryIdByValue(countryVal);
      stateList[countryId] = stateValues.filter(country => country.countryId === countryId);
    });
    return stateList;
  }

  /**
   * Returns the region id by regionValue
   */
  getRegionValueById(regionVal) {
    return this.templateRegionData.find(region => region.regionName === regionVal).regionId;
  }

  getCountyListPerRegion(countyValues) {
    const countyList = {};
    stateDropdownRegions.forEach(regionVal => {
      const regionId = this.getRegionValueById(regionVal);
      countyList[regionId] = countyValues.filter(region => region.regionId === regionId);
    });
    return countyList;
  }

  getRegionListPerCountry(regionValues, countriesList) {
    const regionList = {};
    countriesList.forEach(country => {
      const currentCountryRegionList = regionValues.filter(region => region.countryId === country.Key);
      if (currentCountryRegionList.length) {
        regionList[country.Key] = currentCountryRegionList;
      }
    });
    return regionList;
  }

  hasValue(value) {
    return value && value !== '';
  }

}
